import { ChevronLeftIcon } from '@app/app/_icons/ChevronLeftIcon';
import { ChevronRightIcon } from '@app/app/_icons/ChevronRightIcon';
import { useTranslations } from 'next-intl';
import { useMemo } from 'react';

interface Props {
    viewMode: string;
    currentMonth: Date;
    currentYear: Date;
    yearRange: number;
    onPrevClick: () => void;
    onNextClick: () => void;
    onHeaderClick: () => void;
}

export function CalendarHeader({
    viewMode,
    currentMonth,
    currentYear,
    yearRange,
    onPrevClick,
    onNextClick,
    onHeaderClick,
}: Props) {
    const t = useTranslations();

    const monthNames = useMemo(() => {
        const months = [
            'January',
            'February',
            'March',
            'April',
            'May',
            'June',
            'July',
            'August',
            'September',
            'October',
            'November',
            'December',
        ];
        return months.map(month => t(`general.datePicker.months.${month.toLowerCase()}`, { defaultValue: month }));
    }, [t]);

    const headerText = useMemo(() => {
        switch (viewMode) {
            case 'days':
                return `${monthNames[currentMonth.getMonth()]} ${currentYear.getFullYear()}`;
            case 'months':
                return `${currentYear.getFullYear()}`;
            case 'years':
                const startYear = currentYear.getFullYear();
                const endYear = currentYear.getFullYear() + yearRange;
                return `${startYear} - ${endYear}`;
            default:
                return '';
        }
    }, [viewMode, monthNames, currentMonth, currentYear, yearRange]);

    return (
        <div className="mb-2 flex items-center justify-between">
            <button
                type="button"
                className="rounded-full p-1 hover:bg-neutral-100"
                onClick={onPrevClick}
                aria-label={t('general.datePicker.prevMonth', { defaultValue: 'Previous month' })}>
                <ChevronLeftIcon className="size-4" />
            </button>
            <button
                type="button"
                className="rounded px-2 py-1 text-md font-medium hover:bg-neutral-100 md:text-sm"
                onClick={onHeaderClick}
                aria-label={t('general.datePicker.changeView', { defaultValue: 'Change view' })}>
                {headerText}
            </button>
            <button
                type="button"
                className="rounded-full p-1 hover:bg-neutral-100"
                onClick={onNextClick}
                aria-label={t('general.datePicker.nextMonth', { defaultValue: 'Next month' })}>
                <ChevronRightIcon className="size-4" />
            </button>
        </div>
    );
}
